import type { OrgUnitRow } from "./orgTypes";

/**
 * ORGANİZASYON ŞEMASI YERLEŞİMİ — ekran (SVG) ve baskı (HTML) için TEK kaynak.
 *
 * ── NEDEN SOLDAN SAĞA ──
 * Şema eskiden yukarıdan-aşağıydı: yapraklar yan yana diziliyordu, dolayısıyla
 * GENİŞLİK = yaprak sayısı × 202px. 24 yapraklı bir organizasyonda ~4800px ediyor,
 * ekrana sığmıyor ve yatay kaydırma zorunlu hâle geliyordu.
 *
 * Soldan sağa yerleşimde GENİŞLİK = DERİNLİK × 204px. Derinlik pratikte 3-5 seviyedir
 * ve büyümez; kadro büyüdükçe şema AŞAĞI uzar. Yatay kaydırma kalkar, kâğıda da doğru
 * yönde taşar (A4 dikey sayfalar alt alta eklenir; yana taşan belge basılamaz).
 *
 * ── ÖKSÜZ BİRİM SESSİZCE DÜŞMEZ ──
 * Ebeveyni listede olmayan birim (süzgeç/yetki nedeniyle) KÖK sayılır. Eskiden yalnız
 * `parentId == null` olanlardan yürünüyordu; ebeveyni görünmeyen bir alt ağaç tamamen
 * kaybolurdu ve kullanıcı bunu fark edemezdi.
 */

export const ORG_M = {
  NODE_W: 172,
  NODE_H: 46,
  /** Seviyeler arası yatay boşluk — dirsek çizgisi buraya oturur. */
  H_GAP: 32,
  /** Kardeşler arası dikey boşluk. */
  V_GAP: 9,
  PAD: 16,
} as const;

export interface OrgNodeBox {
  u: OrgUnitRow;
  x: number;
  y: number;
}

export interface OrgLayout {
  nodes: OrgNodeBox[];
  links: Array<{ id: string; d: string }>;
  width: number;
  height: number;
}

/** Kardeşleri WBS'in DOĞAL (sayısal) sırasına göre dizer: "1.10" > "1.9". */
function wbsCompare(a: OrgUnitRow, b: OrgUnitRow): number {
  const pa = a.wbs.split(".").map(Number), pb = b.wbs.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? -1, y = pb[i] ?? -1;
    if (x !== y) return x - y;
  }
  return 0;
}

export interface OrgTree {
  /** ebeveyn id → sıralı çocuklar. */
  byParent: Map<string, OrgUnitRow[]>;
  /** Kökler: parentId yok VEYA ebeveyni bu listede yok (öksüz). */
  roots: OrgUnitRow[];
}

/** Düz satır listesinden sıralı ağaç kur. Ekran ve baskı AYNI sırayı görsün diye ortak. */
export function buildOrgTree(units: OrgUnitRow[]): OrgTree {
  const ids = new Set(units.map((u) => u.id));
  const byParent = new Map<string, OrgUnitRow[]>();
  const roots: OrgUnitRow[] = [];
  for (const u of units) {
    if (u.parentId && ids.has(u.parentId)) {
      const list = byParent.get(u.parentId) ?? [];
      list.push(u);
      byParent.set(u.parentId, list);
    } else {
      roots.push(u);
    }
  }
  // Kardeşler: WBS yeter — bir çocuk her zaman ebeveyninin tesisini alır, dolayısıyla
  // aynı ebeveynin çocukları TEK kapsamdadır.
  for (const list of byParent.values()) list.sort(wbsCompare);
  /**
   * KÖKLER: önce KAPSAM (tesis), sonra WBS.
   *
   * WBS her tesis kapsamında SIFIRDAN numaralanır (sunucudaki `rebuildWbs`, `where: {tesisId}`);
   * yani global kökün de, A tesisinin kökünün de WBS'i "1"dir. Yalnız WBS'e göre sıralamak
   * bütün "1"leri, sonra bütün "2"leri yan yana getiriyor ve FARKLI ŞİRKETLERİN kökleri iç içe
   * geçiyordu (G1, A1, B1, G2, A2…). Sunucu listeyi zaten kapsam-önce veriyor
   * ("kapsamlar iç içe geçmesin"); istemci onu bozmamalı — aksi hâlde Ağaç Listesi ile Şema
   * aynı veriyi FARKLI sırada gösterir.
   */
  roots.sort((a, b) => {
    const sa = a.tesisId ?? "", sb = b.tesisId ?? "";
    if (sa !== sb) return sa < sb ? -1 : 1;
    return wbsCompare(a, b);
  });
  return { byParent, roots };
}

/**
 * "Vardiya dışı" ETKİN kümesi — bayrak ağaç boyunca MİRASLIDIR.
 *
 * Sunucudaki `shiftPlanEmployeeFilter` bir birimi kapatınca `path` önekiyle TÜM ALT AĞACIN
 * personelini vardiya planından düşürüyor. Yalnız birimin KENDİ bayrağını göstermek, alt
 * birimi "vardiyaya tabi" gibi gösterip personelini plandan düşmüş bırakıyordu.
 *
 * Ekran ve BASKI aynı fonksiyonu çağırır: imzalı bir A4 belgenin, planlayıcının yaptığının
 * tersini söylemesi kabul edilemez.
 */
export function computeShiftOff(units: OrgUnitRow[]): Map<string, "self" | "inherited"> {
  const offPaths = units.filter((u) => !u.isShiftBased).map((u) => u.path);
  const m = new Map<string, "self" | "inherited">();
  for (const u of units) {
    if (!u.isShiftBased) { m.set(u.id, "self"); continue; }
    if (offPaths.some((p) => u.path.startsWith(p))) m.set(u.id, "inherited");
  }
  return m;
}

/**
 * Soldan sağa ağaç yerleşimi.
 *  · x = SEVİYE (kökten uzaklık) — `u.depth` DEĞİL: öksüz bir birim kök sayıldığında
 *    onun `depth` değeri 3 olabilir ve kutu boşluğa kayardı. Yürüyüşte sayılır.
 *  · y = yaprakta sıra, iç düğümde ilk/son çocuğun ORTASI.
 */
export function layoutOrgHorizontal(units: OrgUnitRow[]): OrgLayout {
  const { byParent, roots } = buildOrgTree(units);
  const { NODE_W, NODE_H, H_GAP, V_GAP, PAD } = ORG_M;

  const nodes: OrgNodeBox[] = [];
  const box = new Map<string, OrgNodeBox>();
  const seen = new Set<string>(); // döngü kalkanı (bozuk path verisi ağacı sonsuz gezdirmesin)
  let leafCursor = 0;

  const walk = (u: OrgUnitRow, level: number): number => {
    if (seen.has(u.id)) return box.get(u.id)?.y ?? 0;
    seen.add(u.id);
    const kids = byParent.get(u.id) ?? [];
    let y: number;
    if (kids.length === 0) {
      y = leafCursor * (NODE_H + V_GAP);
      leafCursor += 1;
    } else {
      const ys = kids.map((k) => walk(k, level + 1));
      y = (ys[0]! + ys[ys.length - 1]!) / 2;
    }
    const n: OrgNodeBox = { u, x: level * (NODE_W + H_GAP), y };
    nodes.push(n);
    box.set(u.id, n);
    return y;
  };
  for (const r of roots) walk(r, 0);

  /**
   * KÖKTEN ULAŞILAMAYAN birimler — bozuk `parentId` verisi bir DÖNGÜ oluşturursa
   * (a→b→a) hiçbiri kök sayılmaz ve yürüyüş onlara hiç uğramaz. Yalnız döngü kalkanı
   * koymak yetmez: şema o birimleri SESSİZCE YUTARDI, kullanıcı organizasyonun bir
   * parçasının kaybolduğunu fark edemezdi. Kalanlar kök olarak çizilir.
   */
  for (const u of units) if (!seen.has(u.id)) walk(u, 0);

  const maxX = Math.max(0, ...nodes.map((n) => n.x));
  const maxY = Math.max(0, ...nodes.map((n) => n.y));

  // Dirsek: ebeveynin SAĞ ortasından ortak bir dikey gövdeye, oradan çocuğun SOL ortasına.
  // Kardeşler aynı gövdeyi paylaşır → tek dikey çizgi, dağınık görünüm olmaz.
  const links: Array<{ id: string; d: string }> = [];
  for (const n of nodes) {
    const p = n.u.parentId ? box.get(n.u.parentId) : undefined;
    if (!p) continue;
    const x1 = p.x + PAD + NODE_W, y1 = p.y + PAD + NODE_H / 2;
    const x2 = n.x + PAD, y2 = n.y + PAD + NODE_H / 2;
    const trunk = x1 + H_GAP / 2;
    links.push({ id: n.u.id, d: `M ${x1} ${y1} H ${trunk} V ${y2} H ${x2}` });
  }

  return {
    nodes,
    links,
    width: maxX + NODE_W + PAD * 2,
    height: maxY + NODE_H + PAD * 2,
  };
}

export interface OrgFlatRow {
  u: OrgUnitRow;
  level: number;
  /** Kardeşleri arasında sonuncu mu — bağlantı köşesi "└" mı "├" mi. */
  isLast: boolean;
  /**
   * Bu satırın SOLUNA çizilecek ata olukları; her eleman "o ata son çocuktu" der
   * (son çocuksa altında dikey çizgi DEVAM ETMEZ).
   *
   * Uzunluğu TAM `level - 1`dir, `level` değil: seviye-1 birimin solunda ata oluğu
   * YOKTUR, doğrudan kendi bağlantı köşesi gelir. Kökün kendi `isLast` bayrağı buraya
   * GİRMEZ — kökün solunda çizilecek bir çizgi yok. (İlk sürümde giriyordu ve belgedeki
   * tüm alt satırlar bir oluk fazla sağa kayıyordu.)
   */
  ancestorsLast: boolean[];
}

/** Baskı için: ağacı önce-derinlik sırasında düz listeye açar (seviye bilgisiyle). */
export function flattenOrgTree(units: OrgUnitRow[]): OrgFlatRow[] {
  const { byParent, roots } = buildOrgTree(units);
  const out: OrgFlatRow[] = [];
  const seen = new Set<string>();
  const walk = (list: OrgUnitRow[], level: number, ancestorsLast: boolean[]) => {
    list.forEach((u, i) => {
      if (seen.has(u.id)) return;
      seen.add(u.id);
      const isLast = i === list.length - 1;
      out.push({ u, level, isLast, ancestorsLast });
      // Kök (level 0) kendi bayrağını ÇOCUKLARINA GEÇİRMEZ — bkz. `ancestorsLast`.
      walk(byParent.get(u.id) ?? [], level + 1, level === 0 ? [] : [...ancestorsLast, isLast]);
    });
  };
  walk(roots, 0, []);
  // Kökten ulaşılamayanlar (bozuk parentId → döngü) yine listelenir; şemayla aynı kural.
  const kalan = units.filter((x) => !seen.has(x.id));
  if (kalan.length > 0) walk(kalan, 0, []);
  return out;
}
